import React, { useCallback, useEffect, useMemo } from 'react';
import { PublicKey } from '@solana/web3.js';
import { Typography } from '@material-ui/core';
import { useStyles } from '../pages/useStyles';
import {
  StakeRewardCategory,
  StakeRewardTier,
  URL_SUBMIT_REWARD_LIST,
} from '../utils/solchickConsts';
import SolChicksLogo from '../icons/chicks.svg';
import ButtonWithLoader from './ButtonWithLoader';
import { isAddress } from '../utils/solanaHelper';
import { useSolanaWallet } from '../contexts/SolanaWalletContext';
import useStake from '../hooks/useStake';
import {
  isStakingDisabled,
  StakeErrorCode,
  StakeLockedPoolLength,
  StakeMode,
  StakeStatusCode,
  StakeStepMode,
} from '../utils/stakeHelper';
import ConsoleHelper from '../utils/consoleHelper';
import { CHAIN_ID_SOLANA } from '../lib/consts';
import ShowRewardTx from './ShowRewardTx';

export const RewardPoolContainer = ({
  stakeRewardCategory,
  stakeRewardTier,
  fixedAprPercentage,
  stakeLockedRewardPeriod,
  stakeLockedPoolLength,
  stakeAmount,
  maxStakeSlots,
  rewardsList,
}: {
  stakeRewardCategory: StakeRewardCategory;
  stakeRewardTier: StakeRewardTier;
  fixedAprPercentage: number;
  stakeLockedRewardPeriod: string;
  stakeLockedPoolLength: StakeLockedPoolLength;
  stakeAmount: number;
  maxStakeSlots: number;
  rewardsList: string[];
}) => {
  const classes = useStyles();
  const { publicKey: solanaAddress } = useSolanaWallet();
  const wallet = useSolanaWallet();
  const {
    stake,
    isStakeProcessing,
    stakeStatusCode,
    stakeErrorCode,
    stakeLastError,
    sourceTxId,
  } = useStake(StakeMode.LOCKED, StakeStepMode.STAKE, stakeLockedPoolLength);
  const [stakeErrorMessage] = React.useState('');
  const [stakeRewardList, setStakeRewardList] = React.useState(0);

  const fetchStakeRewardList = useCallback(async () => {
    if (wallet.publicKey) {
      const responseData = await fetch(
        URL_SUBMIT_REWARD_LIST(stakeLockedPoolLength, stakeAmount),
      );
      const jsonData = await responseData.json();
      ConsoleHelper(`fetchStakeRewardList -> ${JSON.stringify(jsonData)}`);
      setStakeRewardList(jsonData && jsonData.data ? jsonData.data.length : 0);
    }
  }, [stakeAmount, stakeLockedPoolLength, wallet.publicKey]);

  const handleStakeButtonClick = () => {
    if (wallet.connected) {
      stake(stakeAmount);
      fetchStakeRewardList().then();
    }
  };

  // noinspection DuplicatedCode
  const stakeStatusMessage = useMemo(() => {
    if (isStakeProcessing || stakeStatusCode !== StakeStatusCode.FAILED) {
      switch (stakeStatusCode) {
        case StakeStatusCode.NONE:
          return '';
        case StakeStatusCode.START:
          return 'Start';
        case StakeStatusCode.TOKEN_AMOUNT_CHECKING:
          return 'Checking token amount';
        case StakeStatusCode.PROCESSING:
          return 'Processing';
        case StakeStatusCode.SUBMITTING:
          return 'Submitting result';
        case StakeStatusCode.SUCCESS:
          return 'Success';
        default:
          return 'Unknown';
      }
    } else {
      switch (stakeErrorCode) {
        case StakeErrorCode.NO_ERROR:
          return 'No error message';
        case StakeErrorCode.CANT_CONNECT_SOLANA:
          return "Can't connect to the Solana network";
        case StakeErrorCode.TOKEN_AMOUNT_NOT_ENOUGH:
          return 'Token amount is not enough';
        case StakeErrorCode.SERVER_INVALID:
          return 'Service unavailable';
        case StakeErrorCode.SOLANA_NO_ASSOC_ACCOUNT:
          return 'There is no associated token account';
        case StakeErrorCode.SUBMIT_FAILED:
          return stakeLastError && stakeLastError > ''
            ? stakeLastError
            : 'Unknown error';
        default:
          return '';
      }
    }
  }, [isStakeProcessing, stakeStatusCode, stakeErrorCode, stakeLastError]);

  useEffect(() => {
    if (!wallet.publicKey) {
      return;
    }
    fetchStakeRewardList().then();
  }, [fetchStakeRewardList, wallet.publicKey]);

  return (
    <div className={classes.rewardContainerBox}>
      <div className={classes.rewardContainerBoxHeading}>
        <div className={classes.rewardContainerBoxHeadingText}>
          {stakeRewardCategory} TIER {stakeRewardTier}
        </div>
        <img
          src={`${stakeRewardCategory.toLowerCase()}_lootbox.png`}
          alt={`${stakeRewardCategory.toLowerCase()} lootbox`}
        />
        <div className={classes.rewardContainerBoxSubheadingText}>
          {rewardsList.map((rewardsListItem) => (
            <div className={classes.rewardContainerBoxList}>
              {rewardsListItem}
              <br />
            </div>
          ))}
        </div>
        <div className={classes.rewardContainerBoxSubheadingText}>
          Left:{' '}
          <strong>
            {stakeRewardList >= maxStakeSlots
              ? 0
              : maxStakeSlots - stakeRewardList}
          </strong>
        </div>
      </div>
      <div className={classes.rewardContainerBoxInfo}>
        <div className={classes.rewardContainerBoxInfoRow}>
          <div className={classes.rewardContainerBoxInfoRowOptionText}>
            Est. APR:
          </div>
          <div className={classes.rewardContainerBoxInfoRowOptionValue}>
            {fixedAprPercentage}%
          </div>
        </div>
        <div className={classes.rewardContainerBoxInfoRow}>
          <div className={classes.rewardContainerBoxInfoRowOptionText}>
            Staking Period:
          </div>
          <div className={classes.rewardContainerBoxInfoRowOptionValue}>
            {stakeLockedRewardPeriod}
          </div>
        </div>
        <div className={classes.rewardContainerBoxInfoRow}>
          <div className={classes.rewardContainerBoxInfoRowOptionText}>
            Deposit:
          </div>
          <div className={classes.rewardContainerBoxInfoRowOptionValue}>
            {stakeAmount}{' '}
            <img
              src={SolChicksLogo}
              alt="SolChicks"
              className={classes.solChicksIconCurrency}
            />
          </div>
        </div>
        <div className={classes.rewardContainerBoxButton}>
          <ButtonWithLoader
            onClick={handleStakeButtonClick}
            disabled={
              isStakingDisabled ||
              !isAddress(solanaAddress as PublicKey | string) ||
              isStakeProcessing ||
              stakeRewardList >= maxStakeSlots
            }
          >
            Stake
          </ButtonWithLoader>
          {!stakeErrorMessage && stakeStatusMessage ? (
            <Typography
              variant="body2"
              color="primary"
              className={classes.statusMessage}
            >
              {stakeStatusMessage}
            </Typography>
          ) : null}
          {sourceTxId ? (
            <div style={{ marginTop: '16px' }}>
              <ShowRewardTx chainId={CHAIN_ID_SOLANA} txId={sourceTxId} />
            </div>
          ) : null}
          {stakeErrorMessage ? (
            <Typography
              variant="body2"
              color="error"
              className={classes.statusMessage}
            >
              {stakeErrorMessage}
            </Typography>
          ) : null}
        </div>
      </div>
    </div>
  );
};
